import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./DeviceHealth.module.css";

interface AlertData {
  status: "critical" | "moderate" | "minor";
  title: string;
  description: string;
  location: string;
  deviceType: string;
  deviceName: string;
}

const mockAlerts: AlertData[] = [
  {
    status: "critical",
    title: "Device Offline",
    description:
      "Camera CAM-ParkingLot-03 has not reported data in the last 25 minutes",
    location: "Deli Uptown",
    deviceType: "Camera",
    deviceName: "ParkingLot-03",
  },
  {
    status: "critical",
    title: "Tamper Detected",
    description:
      "Physical tamper detected on Camera CAM-Entrance-01 at Store #112",
    location: "Deli Downtown",
    deviceType: "Camera",
    deviceName: "1.3",
  },
  {
    status: "critical",
    title: "IAQ CO₂ Spike",
    description: "Access Control",
    location: "Deli Downtown",
    deviceType: "IAQ Sensor",
    deviceName: "Office-IAQ1",
  },
  {
    status: "moderate",
    title: "Short Cycling Detected",
    description:
      "HVAC unit RTU-02 at Warehouse B is short cycling (5 cycles in 15 minutes).",
    location: "Deli Midtown",
    deviceType: "HVAC Unit",
    deviceName: "LobbyHVAC",
  },
  {
    status: "moderate",
    title: "Temperature Out of Range",
    description:
      "Thermostat THERM-03 is reading 89°F — above configured threshold of 78°F.",
    location: "Deli Uptown",
    deviceType: "Thermostat",
    deviceName: "Dining-Therm01",
  },
  {
    status: "minor",
    title: "Low Battery Warning",
    description: "Security Panel",
    location: "Deli Uptown",
    deviceType: "Intrusion Panel",
    deviceName: "Office-Panel",
  },
];

const heatMapData = [
  { location: "Location 1", cameras: 3, thermostats: 12, access: 0, panels: 2 },
  { location: "Location 2", cameras: 4, thermostats: 8, access: 9, panels: 5 },
  { location: "Location 3", cameras: 6, thermostats: 2, access: 2, panels: 5 },
  { location: "Location 4", cameras: 5, thermostats: 2, access: 2, panels: 6 },
  { location: "Location 5", cameras: 2, thermostats: 5, access: 4, panels: 1 },
  {
    location: "Location 5",
    cameras: 12,
    thermostats: 10,
    access: 3,
    panels: 13,
  },
];

export const DeviceHealth = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"overview" | "attention">(
    "overview"
  );
  const [searchQuery, setSearchQuery] = useState("");

  const getStatusClass = (count: number) => {
    if (count === 0) return styles.statusGreen;
    if (count >= 10) return styles.statusRed;
    if (count >= 5) return styles.statusOrange;
    return styles.statusYellow;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('deviceHealth.title')}</h1>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "overview" ? styles.tabActive : ""
              }`}
            onClick={() => setActiveTab("overview")}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 2.5L3.33 5.83V12.5C3.33 16.12 5.87 19.45 10 20.83C14.13 19.45 16.67 16.12 16.67 12.5V5.83L10 2.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
            Overview
          </button>
          <button
            className={`${styles.tab} ${activeTab === "attention" ? styles.tabActive : ""
              }`}
            onClick={() => setActiveTab("attention")}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 6.67V10M10 13.33H10.01M4.16 16.67H15.84C17.13 16.67 17.92 15.2 17.27 14.09L11.43 3.75C10.78 2.64 9.22 2.64 8.57 3.75L2.73 14.09C2.08 15.2 2.87 16.67 4.16 16.67Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {t('deviceHealth.needsAttention')}
            <span className={styles.badge}>06</span>
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.leftSection}>
          <div className={styles.heatMapContainer}>
            <div className={styles.heatMapHeader}>
              <div></div>
              <div className={styles.deviceHeaders}>
                <span>{t('deviceHealth.cameras')}</span>
                <span>{t('deviceHealth.thermostats')}</span>
                <span>{t('deviceHealth.accessPoints')}</span>
                <span>{t('deviceHealth.intrusionPanels')}</span>
              </div>
            </div>
            <div className={styles.heatMapBody}>
              {heatMapData.map((row, idx) => (
                <div key={idx} className={styles.heatMapRow}>
                  <div className={styles.locationLabel}>{row.location}</div>
                  <div className={styles.heatMapCells}>
                    <div
                      className={`${styles.cell} ${getStatusClass(
                        row.cameras
                      )}`}
                    >
                      {row.cameras} {t('deviceHealth.alerts')}
                    </div>
                    <div
                      className={`${styles.cell} ${getStatusClass(
                        row.thermostats
                      )}`}
                    >
                      {row.thermostats} {t('deviceHealth.alerts')}
                    </div>
                    <div
                      className={`${styles.cell} ${getStatusClass(row.access)}`}
                    >
                      {row.access} {t('deviceHealth.alerts')}
                    </div>
                    <div
                      className={`${styles.cell} ${getStatusClass(row.panels)}`}
                    >
                      {row.panels} {t('deviceHealth.alerts')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.rightSection}>
          <h2 className={styles.chartTitle}>{t('deviceHealth.deviceHealthByStatus')}</h2>
          <div className={styles.chartContainer}>
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <div
                  className={`${styles.legendColor} ${styles.colorGreen}`}
                ></div>
                <span>{t('deviceHealth.noIssues')}</span>
              </div>
              <div className={styles.legendItem}>
                <div
                  className={`${styles.legendColor} ${styles.colorYellow}`}
                ></div>
                <span>{t('deviceHealth.moderate')}</span>
              </div>
              <div className={styles.legendItem}>
                <div
                  className={`${styles.legendColor} ${styles.colorOrange}`}
                ></div>
                <span>{t('deviceHealth.severe')}</span>
              </div>
              <div className={styles.legendItem}>
                <div
                  className={`${styles.legendColor} ${styles.colorRed}`}
                ></div>
                <span>{t('deviceHealth.critical')}</span>
              </div>
            </div>
            <div className={styles.pieChart}>
              <svg viewBox="0 0 200 200" className={styles.pieSvg}>
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#30a46c"
                  strokeWidth="40"
                  strokeDasharray="126 377"
                  transform="rotate(-90 100 100)"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#ffc53d"
                  strokeWidth="40"
                  strokeDasharray="75 377"
                  strokeDashoffset="-126"
                  transform="rotate(-90 100 100)"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#f76b15"
                  strokeWidth="40"
                  strokeDasharray="75 377"
                  strokeDashoffset="-201"
                  transform="rotate(-90 100 100)"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#e54d2e"
                  strokeWidth="40"
                  strokeDasharray="101 377"
                  strokeDashoffset="-276"
                  transform="rotate(-90 100 100)"
                />
                <text x="45" y="105" className={styles.pieLabel}>
                  20%
                </text>
                <text x="130" y="75" className={styles.pieLabel}>
                  19%
                </text>
                <text x="45" y="55" className={styles.pieLabel}>
                  15%
                </text>
                <text x="130" y="130" className={styles.pieLabel}>
                  15%
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.recentActivity}>
        <h2 className={styles.sectionTitle}>{t('deviceHealth.recentActivity')}</h2>
        <div className={styles.searchBar}>
          <div className={styles.searchInput}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle
                cx="8.5"
                cy="8.5"
                r="5.5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M12.5 12.5L16.5 16.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="text"
              placeholder={t('deviceHealth.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className={styles.filterButton}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M4 6h12M6 10h8M8 14h4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {t('deviceHealth.filter')}
          </button>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('deviceHealth.status')}</th>
                <th>{t('deviceHealth.alertTitle')}</th>
                <th>{t('deviceHealth.alertDescription')}</th>
                <th>{t('deviceHealth.location')}</th>
                <th>{t('deviceHealth.deviceType')}</th>
                <th>{t('deviceHealth.deviceName')}</th>
              </tr>
            </thead>
            <tbody>
              {mockAlerts
                .filter(
                  (alert) =>
                    searchQuery === "" ||
                    alert.title
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase()) ||
                    alert.description
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase())
                )
                .map((alert, idx) => (
                  <tr key={idx}>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[
                          `status${alert.status.charAt(0).toUpperCase() +
                          alert.status.slice(1)
                          }`
                          ]
                          }`}
                      >
                        {alert.status.charAt(0).toUpperCase() +
                          alert.status.slice(1)}
                      </span>
                    </td>
                    <td className={styles.mediumText}>{alert.title}</td>
                    <td className={styles.descriptionText}>
                      {alert.description}
                    </td>
                    <td className={styles.mediumText}>{alert.location}</td>
                    <td className={styles.mediumText}>{alert.deviceType}</td>
                    <td className={styles.mediumText}>{alert.deviceName}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
